import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CartProvider, useCart } from "../context/CartContext";
import { PRODUCTS } from "../mock";
import { ChevronLeft, ShoppingBag, Heart, Truck, ShieldCheck, Leaf, Plus, Minus } from "lucide-react";

const Inner = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const product = PRODUCTS.find((p) => p.slug === slug) || PRODUCTS[0];
  const [qty, setQty] = useState(1);

  const related = PRODUCTS.filter((p) => p.slug !== product.slug).slice(0, 4);

  const onAdd = () => {
    addItem(product, qty);
    openCart();
  };

  return (
    <>
      <Header />

      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 pt-8 text-xs text-stone-500">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 hover:text-stone-800">
          <ChevronLeft size={14} /> Back
        </button>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-stone-800">Shop</Link>
        <span className="mx-2">/</span>
        <span className="text-stone-700">{product.name}</span>
      </div>

      <section className="py-10 lg:py-14">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="bg-stone-50 aspect-square relative overflow-hidden">
            <img src={product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute top-4 left-4 flex gap-2">
              {product.badges?.map((b) => (
                <span key={b} className="text-[10px] tracking-widest uppercase bg-white/95 text-stone-800 px-2 py-1 border border-stone-200">{b}</span>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-stone-500">{product.short}</div>
            <h1 className="font-display text-5xl text-[#c8232c] mt-2">{product.name}</h1>
            <div className="nut-divider-left" />

            <div className="mt-6 flex items-baseline gap-4">
              <div className="text-3xl font-semibold text-stone-900">&euro;{product.price.toFixed(2)}</div>
              <div className="text-sm text-stone-500">{product.weight}</div>
            </div>

            <p className="mt-6 text-stone-600 leading-relaxed">{product.blurb}</p>

            <ul className="mt-6 space-y-2 text-sm text-stone-700">
              <li><span className="text-stone-500">Origin:</span> {product.origin}</li>
              <li><span className="text-stone-500">Net weight:</span> {product.weight}</li>
              <li><span className="text-stone-500">Storage:</span> Cool, dry place &mdash; reseal after opening</li>
            </ul>

            <div className="mt-8 flex items-center gap-4">
              <div className="inline-flex items-center border border-stone-300">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-11 flex items-center justify-center text-stone-600"><Minus size={14} /></button>
                <span className="w-10 text-center text-sm">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-10 h-11 flex items-center justify-center text-stone-600"><Plus size={14} /></button>
              </div>
              <button onClick={onAdd} className="btn-primary flex-1 justify-center max-w-sm">
                <ShoppingBag size={16} /> Add to cart
              </button>
              <button className="w-11 h-11 border border-stone-300 text-stone-600 hover:text-[#c8232c] hover:border-[#c8232c] flex items-center justify-center" aria-label="Wishlist">
                <Heart size={16} />
              </button>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 text-xs text-stone-600">
              <div className="flex flex-col items-center text-center gap-2 p-3 border border-stone-200">
                <Truck size={18} className="text-[#c8232c]" />
                Free EU shipping &gt; &euro;49
              </div>
              <div className="flex flex-col items-center text-center gap-2 p-3 border border-stone-200">
                <ShieldCheck size={18} className="text-[#c8232c]" />
                Quality guaranteed
              </div>
              <div className="flex flex-col items-center text-center gap-2 p-3 border border-stone-200">
                <Leaf size={18} className="text-[#c8232c]" />
                Traceable origin
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-stone-50/60 border-t border-stone-200">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl md:text-4xl text-[#c8232c] text-center">You might also like</h2>
          <div className="nut-divider" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-10">
            {related.map((p) => (
              <div key={p.id} className="product-card bg-white">
                <Link to={`/product/${p.slug}`} className="block">
                  <div className="img-wrap relative overflow-hidden bg-stone-50 aspect-square">
                    <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy"/>
                  </div>
                  <div className="text-center pt-4 pb-3">
                    <h3 className="font-display text-xl text-[#c8232c]">{p.name}</h3>
                    <div className="text-sm text-stone-700 mt-1">&euro;{p.price.toFixed(2)}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

const ProductDetail = () => (
  <CartProvider>
    <Inner />
  </CartProvider>
);

export default ProductDetail;
